import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import REGIONS from "@/data/italyRegions.json";

/* The only region live in this phase: always highlighted in orange. */
const ACTIVE_REGION = "Marche";
export const ACTIVE_REGION_EVENT = "itly-active-region";

/* Tell the backdrop which region the user is exploring on the map (null = none). */
export function setActiveRegion(name) {
    window.dispatchEvent(new CustomEvent(ACTIVE_REGION_EVENT, { detail: name || null }));
}

/* "Giro d'Italia" order, north → south, used to light one region per section while scrolling. */
const TOUR = [
    "Valle d'Aosta/Vallée d'Aoste", "Piemonte", "Liguria", "Lombardia", "Trentino-Alto Adige/Südtirol",
    "Veneto", "Friuli-Venezia Giulia", "Emilia-Romagna", "Toscana", "Umbria", "Marche", "Lazio",
    "Abruzzo", "Molise", "Campania", "Puglia", "Basilicata", "Calabria", "Sicilia", "Sardegna",
];

/* All 20 regions as puzzle pieces in a repeating tile (5 columns × 4 rows, jittered). */
const TILE_W = 1500;
const TILE_H = 1200;
const TONES = ["r-a", "r-b", "r-c"];
const JITTER = [[-30, 20], [25, -35], [-15, 40], [35, 10], [-40, -20], [10, 30], [30, -25], [-25, 15], [20, 35], [-35, -10]];
const ROTS = [-14, 9, -6, 17, -20, 12, -9, 6, -16, 11];

function bbox(d) {
    const n = d.match(/-?\d+\.?\d*/g).map(Number);
    const xs = n.filter((_, i) => i % 2 === 0);
    const ys = n.filter((_, i) => i % 2 === 1);
    return [Math.min(...xs), Math.min(...ys), Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)];
}

function buildPieces() {
    return TOUR.map((name, i) => {
        const r = REGIONS.find((g) => g.name === name);
        const [bx, by, bw, bh] = bbox(r.path);
        const col = i % 5;
        const row = Math.floor(i / 5);
        const [jx, jy] = JITTER[i % JITTER.length];
        const x = 150 + col * 300 + jx + (row % 2 ? 90 : 0);
        const y = 150 + row * 300 + jy;
        const scale = Math.min(1.9, 150 / Math.max(bw, bh));
        return {
            name,
            d: r.path,
            tone: name === ACTIVE_REGION ? "r-active" : TONES[i % TONES.length],
            transform: `translate(${x} ${y}) rotate(${ROTS[i % ROTS.length]}) scale(${scale.toFixed(2)}) translate(${(-(bx + bw / 2)).toFixed(1)} ${(-(by + bh / 2)).toFixed(1)})`,
        };
    });
}

/* Site-wide backdrop for ITLY and ITLY HUB: Italy's 20 regions scattered like puzzle pieces down the whole page.
   The section in view lights one region (a tour of Italy as you scroll); the map gem lights its region too. */
export default function SiteBackdrop() {
    const { pathname } = useLocation();
    const isAdmin = pathname.startsWith("/admin");
    const pieces = useMemo(buildPieces, []);
    const [lit, setLit] = useState(null);
    const [tourStop, setTourStop] = useState(null);

    useEffect(() => {
        if (isAdmin) delete document.documentElement.dataset.bg;
        else document.documentElement.dataset.bg = "pieces";
    }, [isAdmin]);

    useEffect(() => {
        const onRegion = (e) => setLit(e.detail);
        window.addEventListener(ACTIVE_REGION_EVENT, onRegion);
        return () => window.removeEventListener(ACTIVE_REGION_EVENT, onRegion);
    }, []);

    // Tour: the section at the viewport centre picks a region (north → south); map exit resets the gem light
    useEffect(() => {
        setLit(null);
        setTourStop(null);
        if (isAdmin) return undefined;
        const sections = Array.from(document.querySelectorAll("section[id]"));
        const last = Math.max(sections.length - 1, 1);
        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    if (e.target.id === "mappa" && !e.isIntersecting) setLit(null);
                    if (!e.isIntersecting) continue;
                    const i = sections.indexOf(e.target);
                    setTourStop(TOUR[Math.round((i * (TOUR.length - 1)) / last)]);
                }
            },
            { rootMargin: "-40% 0px -50% 0px", threshold: 0 },
        );
        sections.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, [pathname, isAdmin]);

    if (isAdmin) return null;
    return (
        <svg className="itly-backdrop" aria-hidden="true" data-testid="site-backdrop" data-tour-stop={tourStop || ""} xmlns="http://www.w3.org/2000/svg">
            <defs>
                <pattern id="itly-pieces" width={TILE_W} height={TILE_H} patternUnits="userSpaceOnUse">
                    {pieces.map((p) => (
                        <path
                            key={p.name}
                            d={p.d}
                            className={`${p.tone}${tourStop === p.name ? " is-tour" : ""}${lit === p.name ? " is-lit" : ""}`}
                            transform={p.transform}
                            data-region={p.name}
                        />
                    ))}
                </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#itly-pieces)" />
        </svg>
    );
}
